import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend,
);

const YEARS = ["2017", "2018", "2019", "2020", "2021", "2022"];

// ─── Legend pill ────────────────────────────────────────────────────────────
const LegPill = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-1.5 text-[11px] text-[#5A7BA6]">
    <span
      className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
      style={{ background: color }}
    />
    {label}
  </div>
);

// ─── Insight tile ─────────────────────────────────────────────────────────────
const Insight = ({
  value,
  label,
  accent,
}: {
  value: string;
  label: string;
  accent: string;
}) => (
  <div className="flex-1 min-w-[100px] bg-[#F4F7FB] rounded-xl p-3">
    <p
      className="font-extrabold text-xl leading-none font-['Syne',sans-serif]"
      style={{ color: accent }}
    >
      {value}
    </p>
    <p className="text-[10px] text-[#5A7BA6] font-medium mt-1 uppercase tracking-wide">
      {label}
    </p>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
export const GrowthMonitor = () => {
  const enrollData = {
    labels: YEARS,
    datasets: [
      {
        label: "First Term",
        data: [65, 70, 180, 100, 260, 260],
        backgroundColor: "#012063",
        borderRadius: 5,
        barPercentage: 0.7,
      },
      {
        label: "Second Term",
        data: [70, 80, 200, 50, 262, 262],
        backgroundColor: "#0096D1",
        borderRadius: 5,
        barPercentage: 0.7,
      },
      {
        label: "Third Term",
        data: [100, 105, 198, 267, 267, 267],
        backgroundColor: "#00C6A2",
        borderRadius: 5,
        barPercentage: 0.7,
      },
    ],
  };

  const revenueData = {
    labels: YEARS,
    datasets: [
      {
        label: "Tuition Revenue",
        data: [0.5, 2, 1.5, 2.5, 2.5, 2.5],
        borderColor: "#0096D1",
        backgroundColor: "rgba(0,150,209,0.08)",
        fill: true,
        tension: 0.45,
        pointBackgroundColor: "#0096D1",
        pointRadius: 5,
        pointHoverRadius: 7,
        borderWidth: 2.5,
      },
      {
        label: "Supplies Revenue",
        data: [1, 1, 1, 2, 2, 2],
        borderColor: "#00C6A2",
        backgroundColor: "rgba(0,198,162,0.07)",
        fill: true,
        tension: 0.45,
        pointBackgroundColor: "#00C6A2",
        pointRadius: 5,
        pointHoverRadius: 7,
        borderWidth: 2.5,
        borderDash: [6, 4],
      },
    ],
  };

  const barOpts: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#5A7BA6" },
      },
      y: {
        grid: { color: "rgba(1,32,99,0.05)" },
        ticks: { font: { size: 10 }, color: "#5A7BA6" },
        beginAtZero: true,
      },
    },
  };

  const lineOpts: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, color: "#5A7BA6" },
      },
      y: {
        grid: { color: "rgba(1,32,99,0.05)" },
        ticks: {
          font: { size: 10 },
          color: "#5A7BA6",
          callback: (v: any) => `$${v}M`,
        },
        beginAtZero: true,
      },
    },
  };

  return (
    <div className="font-['DM_Sans',sans-serif] text-[#012063] p-2">
      {/* Header */}
      <div className="mb-6">
        <p className="text-sm text-[#5A7BA6] leading-relaxed">
          Track enrolment growth across terms and monitor revenue streams to
          make informed strategic decisions.
        </p>
      </div>

      {/* Insight strip */}
      <div className="flex gap-3 flex-wrap mb-8">
        <Insight value="+300%" label="Enrolment growth" accent="#0096D1" />
        <Insight value="2022" label="Peak year" accent="#00C6A2" />
        <Insight value="+400%" label="Revenue growth" accent="#FF6B35" />
        <Insight value="3" label="Active terms" accent="#7B2FBE" />
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-2 gap-6">
        {/* Enrolment Bar Chart */}
        <div className="bg-white rounded-2xl border border-[#012063]/10 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="font-['Syne',sans-serif] font-bold text-[14px] text-[#012063]">
                Enrolment Growth per Term
              </p>
              <p className="text-[11px] text-[#5A7BA6] mt-0.5">
                Students enrolled by academic year
              </p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap mb-3">
            <LegPill color="#012063" label="First Term" />
            <LegPill color="#0096D1" label="Second Term" />
            <LegPill color="#00C6A2" label="Third Term" />
          </div>
          <div className="h-[220px] w-full">
            <Bar data={enrollData} options={barOpts} />
          </div>
        </div>

        {/* Revenue Line Chart */}
        <div className="bg-white rounded-2xl border border-[#012063]/10 p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="font-['Syne',sans-serif] font-bold text-[14px] text-[#012063]">
                Revenue Growth Trend
              </p>
              <p className="text-[11px] text-[#5A7BA6] mt-0.5">
                Annual revenue by stream (USD millions)
              </p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap mb-3">
            <LegPill color="#0096D1" label="Tuition Revenue" />
            <LegPill color="#00C6A2" label="Supplies Revenue" />
          </div>
          <div className="h-[220px] w-full">
            <Line data={revenueData} options={lineOpts} />
          </div>
        </div>
      </div>

      {/* Year-over-year table */}
      <div className="mt-6 bg-white rounded-2xl border border-[#012063]/10 p-5">
        <p className="font-['Syne',sans-serif] font-bold text-[14px] text-[#012063] mb-4">
          Year-over-Year Summary
        </p>
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#5A7BA6] font-medium">
                <th className="text-left py-2 pr-4">Year</th>
                {YEARS.map((y) => (
                  <th key={y} className="text-right py-2 px-2">
                    {y}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                {
                  label: "Term 1",
                  data: [65, 70, 180, 100, 260, 260],
                  color: "#012063",
                },
                {
                  label: "Term 2",
                  data: [70, 80, 200, 50, 262, 262],
                  color: "#0096D1",
                },
                {
                  label: "Term 3",
                  data: [100, 105, 198, 267, 267, 267],
                  color: "#00C6A2",
                },
              ].map((row) => (
                <tr key={row.label} className="border-t border-[#012063]/05">
                  <td className="py-2.5 pr-4">
                    <span
                      className="inline-flex items-center gap-1.5 text-[11px] font-medium"
                      style={{ color: row.color }}
                    >
                      <span
                        className="w-2 h-2 rounded-sm"
                        style={{ background: row.color }}
                      />
                      {row.label}
                    </span>
                  </td>
                  {row.data.map((v, i) => (
                    <td
                      key={i}
                      className="text-right py-2.5 px-2 font-medium text-[#012063]"
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
